import React, { Component } from 'react'
import PropTypes from 'prop-types'
import styled, { css } from 'styled-components'
import {
  Flex,
  Text,
  BlockLink,
  getPaletteColor,
  Box,
  Relative,
  Absolute,
} from 'pcln-design-system'

const FlexDatesTileWrapper = styled(BlockLink)`
  display: flex;
  flex-direction: column;
  min-width: 104px;
  max-width: 156px;
  height: 100%;
  padding: 4px;
  border-radius: ${({ theme }) => theme.borderRadii.xl};
  background-color: ${getPaletteColor('background.lightest')};
  text-decoration: none;
  position: relative;
  border: ${props =>
    props.isOriginalDepartDate
      ? `1px solid ${getPaletteColor('primary.base')(props)}`
      : `1px solid ${getPaletteColor('border.base')(props)}`};

  &:focus,
  &:hover,
  &:visited,
  &:link,
  &:active {
    text-decoration: none;
    box-shadow: ${({ theme }) => theme.shadows.md};
  }
`

const DateText = styled(Text)`
  font-size: 12px;
  text-align: left;
  line-height: 18px;
  font-weight: 500;
`

const DayText = styled(Text)`
  font-size: 10px;
  text-align: left;
  line-height: 18px;
  font-weight: 500;
`

const PriceText = styled(Text)`
  font-size: 24px;
  line-height: 24px;
  font-weight: 700;
  width: -webkit-fill-available;
  margin-top: 6px;
  bottom: ${props => !props.displayCheaperText && '0'};
  ${props => props.hasBorder && 'position: absolute'};
`

const NoPriceText = styled(Text)`
  line-height: 16px;
  text-align: end;
  font-size: 12px;
  width: 78px;

  ${props => props.theme.flyQueries.tablet} {
    width: 99px;
  }
`

const CurrencySymbol = styled(Text.span)`
  vertical-align: top;
  font-size: 10px;
  line-height: 16px;
`

const FromText = styled(Text)`
  font-size: 10px;
  line-height: 15px;
`

const StyledFlex = styled(Flex)`
  height: inherit;
`

const TileContainer = styled(Flex)`
  flex-direction: column;
  margin-right: 8px;
  max-height: 85px;
  flex-basis: 156px;
  &:last-of-type {
    margin-right: 0;
  }
`

const StyledCheaperText = styled(Text)`
  max-width: 42px;
  text-align: right;

  ${props => props.theme.flyQueries.tablet} {
    white-space: nowrap;
    max-width: 70px;
  }
`

const PriceBox = styled(Box)`
  ${({ displayCheaperText }) =>
    displayCheaperText &&
    css`
      position: absolute;
      right: 2px;
      bottom: 2px;
    `}
`

class LowestPriceFlexDatesTileVariant extends Component {
  render() {
    const {
      flexDate,
      flexDatePriceExists,
      isOriginalDepartDate,
      currencySymbol,
      flexDateDaysDiff,
      flexDateSearchUrl,
      numStops,
      savings,
      hasLowestPrice,
      displaySavings,
    } = this.props
    const flexDateSplit = flexDate.split(' ')
    const flexDay = flexDateSplit[0]
    const flexDateNumber = flexDateSplit[1]
    const noFareText = 'Get Price'
    const flexDatePrice = Math.ceil(this.props.flexDatePrice)
    const gaTaggingLabel = `${flexDateDaysDiff} Day -> ${
      flexDatePriceExists ? flexDatePrice : 'N/A'
    }`

    const stopsText = numStops === 0 ? 'Nonstop' : ''

    const displayCheaperText = hasLowestPrice && displaySavings

    return (
      <TileContainer>
        <FlexDatesTileWrapper
          className={'flex-date-tile-wrapper'}
          isOriginalDepartDate={isOriginalDepartDate}
          onClick={e =>
            !isOriginalDepartDate &&
            this.props.navigateToFlexDateUrl(
              e,
              flexDateSearchUrl,
              gaTaggingLabel
            )
          }
        >
          <StyledFlex justifyContent='space-between'>
            <Box mb='2px'>
              <DayText>{flexDay}</DayText>
              <DateText>{flexDateNumber}</DateText>
            </Box>
            <Relative>
              {stopsText && flexDatePriceExists && (
                <FromText align={'right'} bold>
                  {stopsText}
                </FromText>
              )}
              {displayCheaperText && (
                <React.Fragment>
                  <StyledCheaperText
                    color='darkOrange'
                    bold
                    fontSize='10px'
                  >{`$${savings} cheaper`}</StyledCheaperText>
                  {flexDatePriceExists && (
                    <PriceText
                      displayCheaperText={displayCheaperText}
                      color='pricePrimary.base'
                      align={'right'}
                    >
                      <CurrencySymbol>{currencySymbol}</CurrencySymbol>
                      {flexDatePrice}
                    </PriceText>
                  )}
                </React.Fragment>
              )}
              {flexDatePriceExists && !displayCheaperText && (
                <PriceBox displayCheaperText={displaySavings}>
                  <PriceText
                    displayCheaperText={displayCheaperText}
                    color='pricePrimary.base'
                    align={'right'}
                  >
                    <CurrencySymbol>{currencySymbol}</CurrencySymbol>
                    {flexDatePrice}
                  </PriceText>
                </PriceBox>
              )}
              {!flexDatePriceExists && (
                <Absolute right={0} bottom={0}>
                  <NoPriceText
                    color='priceSecondary.light'
                    align={'bottom'}
                    m={'2px'}
                  >
                    {noFareText}
                  </NoPriceText>
                </Absolute>
              )}
            </Relative>
          </StyledFlex>
        </FlexDatesTileWrapper>
      </TileContainer>
    )
  }
}

FlexDatesTileWrapper.defaultProps = {
  isOriginalDepartDate: false,
}

PriceText.defaultProps = {
  flexDatePriceExists: true,
}

LowestPriceFlexDatesTileVariant.propTypes = {
  currencySymbol: PropTypes.string,
  flexDate: PropTypes.string.isRequired,
  flexDatePriceExists: PropTypes.bool,
  flexDateDaysDiff: PropTypes.number,
  flexDatePrice: PropTypes.number,
  isOriginalDepartDate: PropTypes.bool,
  flexDateSearchUrl: PropTypes.string,
  navigateToFlexDateUrl: PropTypes.func.isRequired,
  flexDatesStopsOWVariant: PropTypes.string,
  numStops: PropTypes.number,
  tripType: PropTypes.string,
  hasLowestPrice: PropTypes.bool,
  displaySavings: PropTypes.bool,
  savings: PropTypes.number,
}

export { LowestPriceFlexDatesTileVariant }
